// Rules-engine shared types — the language-neutral rule grammar and the
// presentation-agnostic configuration state it evaluates over (see
// RULES-ENGINE-DESIGN.md). Nothing here assumes a JS runtime: rules are plain
// JSON data (WHEN <conditions> THEN <effects>) so the same rule sets can later
// be evaluated by the C# revalidation evaluator against shared conformance
// fixtures. Facts and effects are deliberately enumerated — a new fact or
// effect is a deliberate code change, never authored scripting.

/** A resolved fact value. Facts that don't apply resolve to null (never undefined)
 *  so equality comparisons stay well-defined across languages. */
export type FactValue = string | number | boolean | null;

/** Comparison ops implemented in phase 1. The numeric family below is reserved
 *  in the grammar but unbuilt — see RESERVED_OPS. */
export type ComparisonOp = "equals" | "notEquals" | "in";

/** Grammar-ready numerics, not built. Reserved for aggregate/budget rules
 *  (max N addons, total weight ≤ capacity). Using one is a hard authoring
 *  error today ("reserved, not implemented") so adding them later is additive,
 *  not breaking. */
export const RESERVED_OPS = ["count", "gt", "gte", "lt", "lte"] as const;

/** Enumerated relations the `exists` quantifier ranges over. Transitivity is
 *  packaged inside the relation ("assembly"), never in the grammar itself. */
export type Relation = "slots" | "connected" | "assembly" | "group" | "scene";

/** Condition grammar. `exists` is the only quantifier: "there exists an
 *  instance, related to this one via Relation, satisfying `where`". */
export type Condition =
  | { fact: string; op: "equals" | "notEquals"; value: FactValue }
  | { fact: string; op: "in"; values: FactValue[] }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition }
  | { exists: { in: Relation; where: Condition } };

/** Validation severity. `error` gates add-to-cart / submit / PDF;
 *  `warning` / `info` inform only. */
export type Severity = "error" | "warning" | "info";

/** Where a flag renders: on the instance, one of its surfaces/hooks, or the scene. */
export type FlagTarget =
  | { type: "instance" }
  | { type: "surface"; surfaceKey: string }
  | { type: "hook"; hookKey: string }
  | { type: "scene" };

/** Effect grammar — two families. Constraints change/prevent state via the
 *  overlay; the `flag` validation describes state. `seedMaterial` is the
 *  transition-shaped constraint: it is reported as a pending seed for the
 *  caller to write into canonical state on condition transition, never an
 *  overlay entry. `blockAttachment` evaluates but is only *recorded* into the
 *  overlay — enforcement needs the deferred Unity DragValidation contract. */
export type Effect =
  | { effect: "forceMaterial"; surfaceKey: string; materialId: string }
  | { effect: "seedMaterial"; surfaceKey: string; materialId: string }
  | {
      effect: "restrictMaterials";
      surfaceKey: string;
      allowedMaterialIds?: string[];
      blockedMaterialIds?: string[];
    }
  | { effect: "lockSurface"; surfaceKey: string }
  | { effect: "forceVariant"; variantKey: string }
  | {
      effect: "restrictOptions";
      hookKey: string;
      allowedProductIds?: string[];
      blockedProductIds?: string[];
    }
  | { effect: "autoAttach"; hookKey: string; productId: string }
  | { effect: "blockAttachment"; hookKey?: string; condition?: Condition }
  | { effect: "flag"; severity: Severity; target?: FlagTarget; messageDvarId?: string };

/** A rule: authored data, evaluated in author order. `appliesWhen` gates the
 *  whole rule on context (dealer/market/designer), evaluated once. `message`
 *  is the authored explanation as a DVar id — translatable, never derived
 *  from rule logic, and never exposing internal names in end-user UI. */
export interface Rule {
  id: string;
  scope: "product" | "scene";
  appliesWhen?: Condition | null;
  when: Condition;
  then: Effect[];
  message?: string | null;
  /** `message` resolved to translated text server-side at embed delivery
   *  (RuleEmbedEndpoints ?lang=). Absent when the rule has no message or the
   *  DVar resolves empty — UIs then fall back to their generic strings. */
  messageText?: string | null;
}

// ---------------------------------------------------------------------------
// Rule templates — symmetric intents, directional execution. The author edits
// the intent; expandTemplates() compiles it deterministically into the
// directional rules the evaluator understands (symmetry by construction).
// ---------------------------------------------------------------------------

export type RuleTemplate =
  | {
      /** "Never mix these materials in this scope" — e.g. no Ash + Oak. */
      template: "mutuallyExclusive";
      id: string;
      surfaceKeys: string[];
      materialIds: string[];
      /** Optional gate: only applies to instances of this product. */
      productId?: string;
      appliesWhen?: Condition | null;
      messageDvarId?: string;
    }
  | {
      /** "All these surfaces carry the same pick." Expanded over the declared
       *  material set (the grammar has no value references, so the matching
       *  finish is enumerated). */
      template: "mustMatch";
      id: string;
      surfaceKeys: string[];
      materialIds: string[];
      productId?: string;
      appliesWhen?: Condition | null;
      messageDvarId?: string;
    }
  | {
      /** "Choice X on hook H requires prerequisite P" — directional restrict
       *  plus flag(error) when already violated. */
      template: "requires";
      id: string;
      hookKey: string;
      /** The product whose placement requires the prerequisite. */
      productId: string;
      prerequisite: Condition;
      /** Optional gate: the rule pair only evaluates on instances of this product. */
      hostProductId?: string;
      appliesWhen?: Condition | null;
      messageDvarId?: string;
    };

// ---------------------------------------------------------------------------
// Configuration state — presentation-agnostic and serializable. The same
// shape underlies the Unity scene, the Viewer slot configurator, the
// image-based configurator, and saved/submitted configurations.
// ---------------------------------------------------------------------------

/** A slot occupant: which product sits on the hook, and the instance carrying
 *  its own surfaces/hooks (slot contents nest — the configuration is a tree). */
export interface SlotContent {
  productId: string;
  instanceId: string;
}

export interface ConfigInstance {
  instanceId: string;
  productId: string;
  /** undefined → "main" (the variantKey fact applies that default). */
  variantKey?: string;
  /** surfaceKey → materialId; only what the user chose (plus authored seeds). */
  materialPicks: Record<string, string>;
  /** hookKey → occupant, or null/absent for an empty slot. */
  slotContents: Record<string, SlotContent | null>;
  /** Designer grouping. */
  groupId?: string;
}

/** Context facts, resolved server-side into the embed config. */
export interface RuleContext {
  designerId?: string;
  market?: string;
  dealerId?: string;
  lang?: string;
  /** Product Tree choices that led here (treeChoice[nodeKey] fact). */
  treeChoices?: Record<string, string>;
}

export interface ConfigurationState {
  instances: ConfigInstance[];
  context: RuleContext;
  /** formId → field → value (formValue[formId][field] fact). */
  formValues: Record<string, Record<string, string>>;
  /** collectionId → picked value (collectionPick[collectionId] fact). */
  collectionPicks: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Catalog — product lookup supplied by the caller. The evaluator never
// fetches; fact resolution for parameterValue / hasConnector / material
// defaults goes through this interface.
// ---------------------------------------------------------------------------

export interface ProductSurfaceInfo {
  surfaceKey: string;
  defaultMaterialId?: string;
  /** Full swatch list, if the caller wants restrictMaterials resolved to it. */
  materialIds?: string[];
}

export interface ProductInfo {
  productId: string;
  /** Authored spec values (parameterValue[key] fact). */
  parameterValues: Record<string, string | number | boolean>;
  /** Connector keys the product's hooks carry (hasConnector[key] fact). */
  connectorKeys: string[];
  surfaces: ProductSurfaceInfo[];
}

export interface RuleCatalog {
  getProduct(productId: string): ProductInfo | undefined;
}

// ---------------------------------------------------------------------------
// Derived overlay — the evaluator's output. Canonical state stores only user
// choices; everything here evaporates when its trigger changes.
// ---------------------------------------------------------------------------

export interface ForcedPickEntry {
  materialId: string;
  ruleId: string;
}

export interface LockedSurfaceEntry {
  ruleId: string;
}

export interface RestrictedMaterialsEntry {
  allowedMaterialIds?: string[];
  blockedMaterialIds?: string[];
  ruleId: string;
  messageDvarId: string | null;
  /** Server-resolved translated text for the rule's message (Rule.messageText). */
  messageText: string | null;
}

export interface ForcedVariantEntry {
  variantKey: string;
  ruleId: string;
}

/** Overlay-instance identity: an autoAttach-ed product is keyed by the stable
 *  pair (hookKey, ruleId) — not a transient instance id — so user
 *  customization on it survives trigger flips. See stableAutoAttachKey(). */
export interface AutoAttachEntry {
  productId: string;
  ruleId: string;
  attachKey: string;
}

export interface RestrictedOptionsEntry {
  allowedProductIds?: string[];
  blockedProductIds?: string[];
  ruleId: string;
  messageDvarId: string | null;
  /** Server-resolved translated text for the rule's message (Rule.messageText). */
  messageText: string | null;
}

/** blockAttachment is recorded only — the free-drag enforcement point (Unity
 *  DragValidation) does not exist yet. */
export interface BlockedAttachmentEntry {
  hookKey: string | null;
  ruleId: string;
  messageDvarId: string | null;
  /** Server-resolved translated text for the rule's message (Rule.messageText). */
  messageText: string | null;
}

export interface FlagEntry {
  ruleId: string;
  severity: Severity;
  messageDvarId: string | null;
  /** Server-resolved translated text for the rule's message (Rule.messageText). */
  messageText: string | null;
  target: FlagTarget;
}

export interface InstanceOverlay {
  /** surfaceKey → forced pick (first-wins per surface). */
  forcedPicks: Record<string, ForcedPickEntry>;
  /** surfaceKey → lock (picker read-only). */
  lockedSurfaces: Record<string, LockedSurfaceEntry>;
  /** surfaceKey → restriction list; a swatch must pass every entry (union). */
  restrictedMaterials: Record<string, RestrictedMaterialsEntry[]>;
  /** First-wins per instance. */
  forcedVariant: ForcedVariantEntry | null;
  /** hookKey → auto-attached content (first-wins per hook). */
  forcedSlotContents: Record<string, AutoAttachEntry>;
  /** hookKey → restriction list; a candidate must pass every entry (union). */
  restrictedOptions: Record<string, RestrictedOptionsEntry[]>;
  blockedAttachments: BlockedAttachmentEntry[];
  /** Validations union — they never conflict. */
  flags: FlagEntry[];
}

export interface Overlay {
  /** instanceId → overlay; entries exist only where a rule wrote something. */
  instances: Record<string, InstanceOverlay>;
  /** Flags from scene-scoped rules / scene-targeted flags. */
  sceneFlags: FlagEntry[];
}

// ---------------------------------------------------------------------------
// Trace, errors, and evaluation result.
// ---------------------------------------------------------------------------

export interface TraceEffectRecord {
  description: string;
  /** Set when first-wins suppressed this effect: the winning rule's id. */
  suppressedBy?: string;
}

export interface TraceEntry {
  ruleId: string;
  /** The instance the rule was evaluated against; absent for scene scope. */
  instanceId?: string;
  fired: boolean;
  /** True when appliesWhen gated the rule out for this evaluation. */
  gated?: boolean;
  effects: TraceEffectRecord[];
  notes: string[];
}

export interface TracePass {
  pass: number;
  entries: TraceEntry[];
}

/** Authoring/support-facing evaluation trace — answers "why is this greyed
 *  out / forced / flagged?". End users only ever see authored messages. */
export interface RuleTrace {
  passes: TracePass[];
  converged: boolean;
  notes: string[];
}

export type EvalErrorKind =
  | "unknownFact"
  | "unknownOp"
  | "unknownRelation"
  | "unknownEffect"
  | "missingInstance"
  | "effectRequiresInstance"
  | "nonConvergence";

/** Structured authoring error. The evaluator records these and keeps going
 *  (the offending rule simply doesn't fire) — with one exception: reserved
 *  numeric ops throw, by design. */
export interface EvalError {
  kind: EvalErrorKind;
  ruleId?: string;
  instanceId?: string;
  message: string;
}

/** ruleId + binding → did the rule's `when` hold on the final pass? Keys are
 *  `${ruleId}@${instanceId}` (or `${ruleId}@$scene`). Callers keep these
 *  between evaluations to detect seed transitions. */
export type ConditionResults = Record<string, boolean>;

/** A seedMaterial whose condition currently holds. The evaluator stays
 *  stateless: the CALLER applies the seed to canonical state when
 *  computeSeedTransitions() shows conditionKey newly became true. */
export interface PendingSeed {
  instanceId: string;
  surfaceKey: string;
  materialId: string;
  ruleId: string;
  conditionKey: string;
}

export interface EvalResult {
  overlay: Overlay;
  trace: RuleTrace;
  errors: EvalError[];
  conditionResults: ConditionResults;
  pendingSeeds: PendingSeed[];
}

/** Interrogative answer: "would candidate X be valid on hook H — and if not,
 *  why?" Reasons carry the authored DVar message plus the rule id (for trace). */
export interface CandidateReason {
  ruleId: string;
  messageDvarId: string | null;
  /** Server-resolved translated text for the rule's message (Rule.messageText). */
  messageText: string | null;
}

export type CandidateResult =
  | { allowed: true }
  | { allowed: false; reasons: CandidateReason[] };
